import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { prepareWeekDetails } from "@/api/plan";
import { Button } from "@/components/ui/Button";
import { useAuthStore } from "@/stores/auth";

const REASONS: Record<string, string> = {
  health_review_required: "planning_quality.health_body",
  strava_source_restricted: "workout_detail.preview_source_desc",
  activity_recorded_on_date: "plan_preparation.activity_recorded",
  preserved_calendar_event: "plan_preparation.preserved_event",
  ambiguous_plan_slot: "plan_preparation.ambiguous_slot",
  no_pending_details: "plan_preparation.no_pending",
  outside_preparation_window: "plan_preparation.outside_window",
  generation_disabled: "plan_preparation.unavailable",
  account_not_eligible: "plan_preparation.account_required",
  planning_profile_incomplete: "plan_preparation.profile_required",
  saved_plan_conflicts_with_profile: "plan_preparation.profile_conflict",
};

export function PreparationReason({ reason }: { reason?: string | null }) {
  const { t } = useTranslation();
  if (!reason || !REASONS[reason] || reason === "no_pending_details") return null;
  return <p className="mt-2 text-sm text-taupe">{t(REASONS[reason])}
    {["planning_profile_incomplete", "account_not_eligible", "saved_plan_conflicts_with_profile"].includes(reason) && <> <Link to={reason === "account_not_eligible" ? "/settings" : "/settings?edit=training#training-profile"} className="underline">{t(reason === "account_not_eligible" ? "nav.settings" : "planning_setup.complete")}</Link></>}
  </p>;
}

/** Explicit paid action; a GET/refetch never starts preparation. */
export function PrepareDetailsButton({ weekNumber, weekStart, disabled = false }: {
  weekNumber: number; weekStart: string; disabled?: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const token = useAuthStore(state => state.token);
  const [message, setMessage] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const mounted = useRef(false);
  const active = useRef({ weekNumber, weekStart, token });
  active.current = { weekNumber, weekStart, token };
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => { setMessage(null); setFailed(false); }, [weekNumber, weekStart, token]);
  const current = (v: typeof active.current) => mounted.current && useAuthStore.getState().token === v.token
    && active.current.weekNumber === v.weekNumber && active.current.weekStart === v.weekStart;
  const refresh = () => {
    for (const key of ["week", "plan", "plan-workouts", "planned-workout", "dashboard", "reviews", "review"])
      void queryClient.invalidateQueries({ queryKey: [key] });
  };
  const mutation = useMutation({
    retry: false,
    mutationFn: (v: typeof active.current) => prepareWeekDetails(v.weekNumber, v.weekStart, v.token),
    onSuccess: (result, v) => {
      if (!current(v)) return;
      const matches = result.week_number === v.weekNumber && result.week_start_date === v.weekStart;
      setFailed(!matches);
      setMessage(t(!matches ? "plan_preparation.target_changed"
        : result.status === "ok" && result.workouts_updated > 0 ? "plan_preparation.success"
        : REASONS[result.reason ?? ""] ?? "plan_preparation.deferred", { count: result.workouts_updated }));
      refresh();
    },
    onError: (error, v) => {
      if (!current(v)) return;
      const response = (error as { response?: { status?: number; data?: { detail?: string | { reason?: string } } } }).response;
      const detail = response?.data?.detail;
      const reason = typeof detail === "string" ? detail : detail?.reason;
      setFailed(true);
      setMessage(t(reason === "plan_target_changed" ? "plan_preparation.target_changed"
        : REASONS[reason ?? ""] ?? (response?.status === 409 ? "plan_preparation.deferred" : "plan_preparation.failed")));
      refresh();
    },
  });
  return <div className="space-y-2">
    <Button type="button" variant="primary" loading={mutation.isPending} disabled={disabled || mutation.isPending}
      onClick={() => { setMessage(null); setFailed(false); mutation.mutate({ weekNumber, weekStart, token }); }}>
      {t(mutation.isPending ? "plan_preparation.preparing" : "plan_preparation.prepare")}
    </Button>
    {mutation.isPending && <p role="status" className="text-sm text-taupe">{t("plan_preparation.wait")}</p>}
    {message && <p role={failed ? "alert" : "status"} className="text-sm">{message}</p>}
  </div>;
}
